import type { DeclaredMemberType, ImageKind, LocationKind } from "@ondo/shared";

/**
 * Constants more than one screen needs.
 *
 * Everything here started life in a single screen's constants file and moved
 * when a second screen wanted it. That is the rule the skill gives: a constant
 * forked across two features is a constant that will drift, and an ONDO id
 * pattern that disagreed with itself would refuse a valid id on one form and
 * accept an invalid one on another.
 */

/** Matches the check constraint on ondo.principals.ondo_id. */
export const ONDO_ID_PATTERN = /^ONDO-[0-9]{4,}$/;

/**
 * A shape check on an email address, and deliberately not a validator.
 *
 * Loose on purpose. What is actually deliverable is not knowable from a regex —
 * quoted local parts, new top-level domains and internationalised hosts are all
 * real — and the strictest pattern anyone writes is the one that refuses a real
 * address with no workaround. The database applies the same shape again, and
 * the true test is that a code arrives.
 *
 * It matters most where it is used: naming a party to ask for a verification.
 * The person on the other end of that address may never have heard of ONDO, and
 * they are exactly who this door was built to reach — so a pattern that turned
 * away an unusual address would lock out the case rather than a typo.
 */
export const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/**
 * What an applicant can say they are. Any number of them.
 *
 * The values are `DeclaredMemberType`, shared with the API and the database
 * enum; the labels are display only. Read the type's own comment before
 * treating any of these as a permission — none of them grants anything, and
 * ticking several grants no more than ticking one.
 *
 * Moved here from signup.constants.ts when the profile started showing the
 * choice back: the tick box on the application form and the row on the account
 * page must say the same word for the same enum value, or somebody who ticked
 * "Artist's Estate" would read "Artist Estate" afterwards and wonder which they
 * had chosen.
 */
export const MEMBERSHIP_TYPES: readonly {
  value: DeclaredMemberType;
  label: string;
}[] = [
  { value: "artist", label: "Artist" },
  { value: "artist_estate", label: "Artist's Estate" },
  { value: "collector", label: "Collector" },
  { value: "gallery", label: "Gallery" },
  { value: "auction_house", label: "Auction House" },
  { value: "art_advisor", label: "Art Advisor" },
  { value: "museum", label: "Museum" },
  { value: "collection_manager", label: "Collection Manager" },
];

/**
 * What a photograph of a work shows, in words.
 *
 * Moved out of register.constants.ts when the work record started showing the
 * photographs the wizard uploads: the caption under a thumbnail and the label
 * on the dropzone must say the same word for the same `image_kind`, or a
 * collector who filed a verso would find it captioned something else.
 */
export const IMAGE_KIND_LABELS: Record<ImageKind, string> = {
  front: "Front",
  back: "Back / verso",
  signature: "Signature",
  stamp: "Stamp",
  gallery_label: "Gallery label",
  edition_number: "Edition number",
  detail: "Detail / condition",
  certificate: "Certificate",
  other: "Other",
};

export const LOCATION_LABELS: Record<LocationKind, string> = {
  displayed: "Displayed",
  in_storage: "In Storage",
  in_transit: "In Transit",
};

/** What the registry says about a work, wherever a work is rendered. */
export const WORK_COPY = {
  /**
   * Shown instead of a location for a work reached through a work grant. The
   * registry is refusing rather than failing to look: where the physical object
   * is kept follows the collection door, never the work door.
   */
  locationWithheld: "Location not shared",
  /**
   * What a work with nobody behind it says.
   *
   * Not "unverified", which reads as a finding. Nobody has looked, and the
   * honest sentence is that nobody has signed.
   */
  noVerifications: "No verifications",
  sharedWithMe: "Shared with you",
  /**
   * Shown where a collection name would go, for a reader who reached the work
   * without reaching its placement — an agent under a mandate, somebody holding
   * a single work grant, the attributed artist.
   *
   * "Not shared with you" rather than a blank or an omitted row, on
   * `locationWithheld`'s argument: a withheld fact is a boundary, and a reader
   * shown nothing where a collection belongs reads a gap in the record instead.
   */
  collectionWithheld: "Not shared with you",
  /** Alt text for a work whose photograph is showing. */
  imageOf: "Photograph of",
  /** Shown in place of a photograph, so an empty frame is not read as a failure. */
  noImage: "No photograph",
} as const;
